import type { NavSectionProps } from 'src/components/nav-section';
import type { Theme, SxProps, Breakpoint } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import { iconButtonClasses } from '@mui/material/IconButton';

import { useBoolean } from 'src/hooks/use-boolean';
import { ApprovalCountBadge, useApprovalCount } from 'src/hooks/use-approval-count';
import { useDebugMode } from 'src/hooks/use-debug-mode';

import { Logo } from 'src/components/logo';
import { useSettingsContext } from 'src/components/settings';

import { Main } from './main';
import { NavMobile } from './nav-mobile';
import { layoutClasses } from '../classes';
import { NavVertical } from './nav-vertical';
import { NavHorizontal } from './nav-horizontal';
import { _account } from '../config-nav-account';
// import { _workspaces } from '../config-nav-workspace';
import { MenuButton } from '../components/menu-button';
import { LayoutSection } from '../core/layout-section';
import { HeaderSection } from '../core/header-section';
import { StyledDivider, useNavColorVars } from './styles';
import { AccountDrawer } from '../components/account-drawer';
import useAuth from 'src/auth/useAuth';

import { NavUserFooter } from '../components/nav-user-footer';
import { NavWalletBalanceWidget } from '../components/nav-wallet-balance-widget';
// import { SettingsButton } from '../components/settings-button';
import { OrganizationsPopover } from '../components/organizations-popover';
import { navData as dashboardNavData } from '../config-nav-dashboard';
import { paths } from 'src/routes/paths';
import { useGetOrganizationQuery } from 'src/generated/graphql';
// import { NotificationsDrawer } from '../components/notifications-drawer';

// ----------------------------------------------------------------------

export type DashboardLayoutProps = {
  sx?: SxProps<Theme>;
  children: React.ReactNode;
  header?: {
    sx?: SxProps<Theme>;
  };
  data?: {
    nav?: NavSectionProps['data'];
  };
};

export function DashboardLayout({ sx, children, header, data }: DashboardLayoutProps) {
  const theme = useTheme();
  const { organizationId } = useAuth();

  const [{ data: orgData }] = useGetOrganizationQuery({
    variables: { organizationId: organizationId || '' },
    pause: !organizationId,
  });
  const valiantEnabled = orgData?.getOrganization?.valiantEnabled ?? false;
  const nearIntentsEnabled = orgData?.getOrganization?.nearIntentsEnabled ?? false;
  const customerSwapsEnabled = orgData?.getOrganization?.customerSwapsEnabled ?? false;
  const batchPayoutsEnabled = orgData?.getOrganization?.batchPayoutsEnabled ?? false;
  const { debugMode } = useDebugMode();
  const { count: approvalCount } = useApprovalCount();
  const makeApprovalBadge = () =>
    approvalCount > 0 ? <ApprovalCountBadge count={approvalCount} /> : undefined;

  const mobileNavOpen = useBoolean();

  const settings = useSettingsContext();

  const navColorVars = useNavColorVars(theme, settings);

  const layoutQuery: Breakpoint = 'lg';

  const rawNavData = data?.nav ?? dashboardNavData;
  // Gate feature nav entries on org flags. When disabled the entry is filtered
  // out so the feature is invisible in the nav (direct route access is also
  // blocked in each section's layout). A caller-supplied `data.nav` override
  // is respected as-is.
  const navData = data?.nav
    ? rawNavData
    : rawNavData.map((section) => ({
        ...section,
        items: section.items
          .filter((item) => {
            if (item.path === paths.dashboard.swap.root) return valiantEnabled;
            if (item.path === paths.dashboard.batchPayouts.root) {
              return batchPayoutsEnabled;
            }
            return true;
          })
          .map((item) => ({
            ...item,
            ...(item.title === 'Custodial' ? { info: makeApprovalBadge() } : {}),
            children: item.children
              ?.filter((child: { path: string }) => {
                if (child.path === paths.dashboard.nearIntents.root) {
                  return nearIntentsEnabled && debugMode;
                }
                if (child.path === paths.dashboard.customerSwaps.root) {
                  return customerSwapsEnabled;
                }
                if (
                  child.path === paths.dashboard.treasuryAutoConversion ||
                  child.path === paths.dashboard.checkout.autoConversion
                ) {
                  return nearIntentsEnabled;
                }
                return true;
              })
              .map((child: { path: string }) =>
                child.path === paths.dashboard.transactions
                  ? { ...child, info: makeApprovalBadge() }
                  : child,
              ),
          })),
      }));

  const isNavMini = settings.navLayout === 'mini';
  const isNavHorizontal = settings.navLayout === 'horizontal';
  const isNavVertical = isNavMini || settings.navLayout === 'vertical';

  return (
    <LayoutSection
      /** **************************************
       * Header
       *************************************** */
      headerSection={
        <HeaderSection
          layoutQuery={layoutQuery}
          disableElevation={isNavVertical}
          slotProps={{
            toolbar: {
              sx: {
                ...(isNavHorizontal && {
                  bgcolor: 'var(--layout-nav-bg)',
                  [`& .${iconButtonClasses.root}`]: {
                    color: 'var(--layout-nav-text-secondary-color)',
                  },
                  [theme.breakpoints.up(layoutQuery)]: {
                    height: 'var(--layout-nav-horizontal-height)',
                  },
                }),
              },
            },
            container: {
              maxWidth: false,
              sx: {
                ...(isNavVertical && { px: { [layoutQuery]: 5 } }),
              },
            },
          }}
          sx={header?.sx}
          slots={{
            topArea: (
              <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
                This is an info Alert.
              </Alert>
            ),
            bottomArea: isNavHorizontal ? (
              <NavHorizontal
                data={navData}
                layoutQuery={layoutQuery}
                cssVars={navColorVars.section}
              />
            ) : null,
            leftArea: (
              <>
                {/* -- Nav mobile -- */}
                <MenuButton
                  onClick={mobileNavOpen.onTrue}
                  sx={{
                    mr: 1,
                    ml: -1,
                    [theme.breakpoints.up(layoutQuery)]: { display: 'none' },
                  }}
                />
                <NavMobile
                  data={navData}
                  open={mobileNavOpen.value}
                  onClose={mobileNavOpen.onFalse}
                  cssVars={navColorVars.section}
                />
                {/* -- Logo -- */}
                {isNavHorizontal && (
                  <Logo
                    sx={{
                      display: 'none',
                      [theme.breakpoints.up(layoutQuery)]: { display: 'inline-flex' },
                    }}
                  />
                )}
                {/* -- Divider -- */}
                {isNavHorizontal && (
                  <StyledDivider
                    sx={{ [theme.breakpoints.up(layoutQuery)]: { display: 'flex' } }}
                  />
                )}
                {/* -- Workspace popover -- */}
                <OrganizationsPopover sx={{ color: 'var(--layout-nav-text-primary-color)' }} />
              </>
            ),
            rightArea: (
              <Box display="flex" alignItems="center" gap={{ xs: 0, sm: 0.75 }}>
                {/* -- Notifications popover -- */}
                {/* <NotificationsDrawer /> */}
                {/* -- Settings button -- */}
                {/* <SettingsButton /> */}
                {/* -- Account drawer -- */}
                <AccountDrawer data={_account} />
              </Box>
            ),
          }}
        />
      }
      /** **************************************
       * Sidebar
       *************************************** */
      sidebarSection={
        isNavHorizontal ? null : (
          <NavVertical
            data={navData}
            isNavMini={isNavMini}
            layoutQuery={layoutQuery}
            cssVars={navColorVars.section}
            onToggleNav={() =>
              settings.onUpdateField(
                'navLayout',
                settings.navLayout === 'vertical' ? 'mini' : 'vertical'
              )
            }
          />
        )
      }
      /** **************************************
       * Footer
       *************************************** */
      footerSection={null}
      /** **************************************
       * Style
       *************************************** */
      cssVars={{
        ...navColorVars.layout,
        '--layout-transition-easing': 'linear',
        '--layout-transition-duration': '120ms',
        '--layout-nav-mini-width': '88px',
        '--layout-nav-vertical-width': '300px',
        '--layout-nav-horizontal-height': '64px',
        '--layout-dashboard-content-pt': theme.spacing(1),
        '--layout-dashboard-content-pb': theme.spacing(8),
        '--layout-dashboard-content-px': theme.spacing(5),
      }}
      sx={{
        [`& .${layoutClasses.hasSidebar}`]: {
          [theme.breakpoints.up(layoutQuery)]: {
            transition: theme.transitions.create(['padding-left'], {
              easing: 'var(--layout-transition-easing)',
              duration: 'var(--layout-transition-duration)',
            }),
            pl: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
          },
        },
        ...sx,
      }}
    >
      <Main isNavHorizontal={isNavHorizontal}>{children}</Main>
      {isNavHorizontal && (
        <Box
          sx={{
            display: { xs: 'none', [layoutQuery]: 'block' },
            position: 'fixed',
            zIndex: 1000,
            left: (t) => t.spacing(2),
            bottom: (t) => t.spacing(2),
            maxWidth: 340,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'var(--layout-nav-bg)',
            boxShadow: (t) => t.customShadows.z8,
            border: (t) => `1px solid ${t.vars.palette.divider}`,
          }}
        >
          {organizationId ? <NavWalletBalanceWidget organizationId={organizationId} /> : null}
          <NavUserFooter sx={{ borderTop: 'none' }} />
        </Box>
      )}
    </LayoutSection>
  );
}
